// Tablier data layer — schema canonique (B-001).
// `Tenanted` est la marque commune cloisonnement local. Toute entité Dexie en hérite.

export interface Tenanted {
  tenantId: string;
}

export type OrderStatus = 'pending' | 'inProgress' | 'done' | 'cancelled';

export interface Product extends Tenanted {
  id?: number;
  name: string;
  priceIndicative: number;
  createdAt: number;
}

export interface Order extends Tenanted {
  id?: number;
  customerName: string;
  status: OrderStatus;
  createdAt: number;
  closedAt?: number;
}

export interface OrderItem extends Tenanted {
  id?: number;
  orderId: number;
  productId: number;
  qty: number;
  createdAt: number;
}

export interface Session extends Tenanted {
  id?: number;
  startedAt: number;
  endedAt?: number;
  location?: string;
}

// v1 = forme historique du data layer Dexie sans tenantId.
// Utilisée uniquement pour reconstruire un état "pré-B-001" lors des tests
// de migration. Aucune écriture v1 ne se produit en exécution réelle.
export const SCHEMA_V1 = {
  products: '++id, createdAt',
  orders: '++id, status, createdAt',
  orderItems: '++id, orderId, createdAt',
  sessions: '++id, startedAt',
} as const;

// v2 = forme cible B-001. Index secondaires `tenantId` + composites `[tenantId+...]`
// pour cloisonnement local + requêtes scoped sans full-scan.
export const SCHEMA_V2 = {
  products: '++id, tenantId, [tenantId+createdAt]',
  orders: '++id, tenantId, status, [tenantId+status], [tenantId+createdAt]',
  orderItems: '++id, tenantId, orderId, [tenantId+orderId]',
  sessions: '++id, tenantId, [tenantId+startedAt]',
} as const;

export const TABLE_NAMES = ['products', 'orders', 'orderItems', 'sessions'] as const;
export type TableName = (typeof TABLE_NAMES)[number];

// --- T1 sous-ticket 1 — DATA-MODEL T1 (post-B-001) ---------------------------
// Ajout non destructif : 4 tables métier (Client, Commande, CommandePizza, Pizza)
// + matrice statuts data canon W.9. Aucune table B-001 modifiée. Migration v2->v3
// purement additive. Multi-tenant strict (index composites en `[tenantId+...]`).

// 8 statuts data persistés. Le 9e statut « Brouillon / saisie » de la doctrine W.9 §3
// est un état UI transient (state React T1Shell sous-ticket 3), jamais persisté en DB.
// Ne pas étendre cette union sans ticket dédié (cf. doctrine TECH-DESIGN-FIRST OS §13).
export type StatutCommande =
  | 'commandee'
  | 'priorisee'
  | 'en_preparation'
  | 'enfournee'
  | 'en_finalisation'
  | 'prete'
  | 'cloturee'
  | 'annulee';

// LIFO max 3 dernières commandes par Client (Q3 acté Hamid 2026-04-30 — non paramétrable v1).
export const MAX_DERNIERES_COMMANDES = 3;

export interface Client extends Tenanted {
  id?: number;
  telephone: string;
  nom: string;
  habitude?: string;
  commandes_recurrentes?: string;
  dernieres_commandes_ids: number[];
  createdAt: number;
  updatedAt: number;
}

// AUCUN champ paiement (whitelist par construction — frontière anti-caisse brief MVP §1.5).
// Snapshots téléphone/nom : writable (mutables) — anonymisation à la suppression du Client
// implémentée sous-ticket 5 (Q2 acté Hamid 2026-04-30).
export interface Commande extends Tenanted {
  id?: number;
  clientId?: number;
  client_telephone_snapshot?: string;
  client_nom_snapshot?: string;
  statut: StatutCommande;
  heure_retrait_proposee: number;
  train_id?: number;
  createdAt: number;
  updatedAt: number;
}

export interface CommandePizza extends Tenanted {
  id?: number;
  commandeId: number;
  pizzaId: number;
  qty: number;
  notes_libres?: string;
  prix_unitaire_indicatif: number;
  createdAt: number;
}

export interface Pizza extends Tenanted {
  id?: number;
  nom: string;
  prix_indicatif: number;
  description?: string;
  actif: boolean;
  createdAt: number;
  updatedAt: number;
}

// v3 = forme cible T1 sous-ticket 1. SCHEMA_V3 = SCHEMA_V2 byte-exact + 4 nouvelles tables.
// IMPORTANT : Dexie supprime toute store omise dans la dernière `version().stores()` —
// les 4 stores B-001 sont donc redéclarées explicitement (Test #1 vérifie activement).
export const SCHEMA_V3 = {
  // Tables B-001 redéclarées byte-exact (NE PAS retirer — Dexie supprime sinon)
  products: '++id, tenantId, [tenantId+createdAt]',
  orders: '++id, tenantId, status, [tenantId+status], [tenantId+createdAt]',
  orderItems: '++id, tenantId, orderId, [tenantId+orderId]',
  sessions: '++id, tenantId, [tenantId+startedAt]',
  // Nouvelles tables T1
  clients: '++id, tenantId, [tenantId+telephone], [tenantId+createdAt]',
  commandes:
    '++id, tenantId, statut, [tenantId+statut], [tenantId+heure_retrait_proposee], [tenantId+createdAt]',
  commandePizzas: '++id, tenantId, commandeId, [tenantId+commandeId]',
  pizzas: '++id, tenantId, [tenantId+actif], [tenantId+createdAt]',
} as const;
